import { ButtonPrimary, ButtonLight } from 'components/Button'
import { RowBetween } from 'components/Row'
import { useActiveWeb3React } from 'hooks'
import React, { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { calculateGasMargin } from 'utils'

import { useTransactionAdder } from 'state/transactions/hooks'
import { Dots } from '../styleds'

import { roundToDecimalPlace } from 'utils/data'
import { Text } from 'rebass'
import TokenLogo from 'pages/ExplorePools/TokenLogo'
import { useMerklDistributor } from 'hooks/merkle/useCampaigns'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  width: 100%;
  padding: 1rem;
  max-height: 75vh;
  overflow-y: auto;

  h2 {
    font-weight: 700;
    color: white;
    font-size: 20px;
    margin-bottom: 12px;
  }
`

const PoolContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
`

const RewardItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 12px;
  padding: 12px;
  background-color: ${({ theme }) => theme.bg1};

  .rewards-header {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .rewards-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
  }

  .rewards-body-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    color: ${({ theme }) => theme.text1};

    .rewards-body-item-label {
      color: ${({ theme }) => theme.text2};
    }
  }
`

const WarningContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  color: ${({ theme }) => theme.text2};
  font-size: 12px;
  background-color: rgba(227, 165, 7, 0.1);
`

export default function ClaimIncentives({ rewards, onDismiss }: { rewards: any[] | undefined; onDismiss: () => void }) {
  const { account, library } = useActiveWeb3React()
  const addTransaction = useTransactionAdder()
  const theme = useTheme()
  const distributorContract = useMerklDistributor()

  const [loadingClaim, setLoadingClaim] = useState<boolean>(false)

  const handleClaim = async () => {
    if (account && library && distributorContract) {
      const estimate = distributorContract.estimateGas.claim
      const method = distributorContract.claim

      const users: any[] = []
      const tokens: any[] = []
      const amounts: any[] = []
      const proofs: any[] = []

      rewards?.forEach((reward) => {
        users.push(account)
        tokens.push(reward.token.address)
        amounts.push(reward.amount)
        proofs.push(reward.proofs)
      })

      const args = [users, tokens, amounts, proofs]

      setLoadingClaim(true)
      await estimate(...args, {})
        .then((estimatedGasLimit: any) => {
          return method(...args, {
            gasLimit: calculateGasMargin(estimatedGasLimit),
          }).then(async (response: any) => {
            addTransaction(response, {
              summary: `Claim Incentives`,
            })
            setLoadingClaim(false)
            onDismiss()
          })
        })
        .catch((error: any) => {
          setLoadingClaim(false)
          console.error(error)
        })
    }
  }
  return (
    <Container>
      <h2>Collect Incentives</h2>

      <Text color={theme.text2} fontSize="14px">
        Collect rewards from incentive programs linked to your liquidity positions.
      </Text>

      {rewards && rewards.length > 0 ? (
        <>
          <PoolContainer>
            {rewards.map((reward, index) => {
              const tokenDecimals = reward.token.decimals
              const claimable = (Number(reward.amount) - Number(reward.claimed)) / Math.pow(10, tokenDecimals)
              const claimed = Number(reward.claimed) / Math.pow(10, tokenDecimals)
              const notClaimable = Number(reward.pending) / Math.pow(10, tokenDecimals)

              return (
                <RewardItem key={`${reward.root}-${index}`}>
                  <div className="rewards-header">
                    <TokenLogo token={reward.token.address} />
                    <span>{reward.token.symbol}</span>
                  </div>
                  <div className="rewards-body">
                    <div className="rewards-body-item">
                      <span className="rewards-body-item-label">Claimable</span>
                      <span>{roundToDecimalPlace(claimable.toString(), '8')}</span>
                    </div>
                    <div className="rewards-body-item">
                      <span className="rewards-body-item-label">Not claimable yet</span>
                      <span>{roundToDecimalPlace(notClaimable.toString(), '8')}</span>
                    </div>
                    <div className="rewards-body-item">
                      <span className="rewards-body-item-label">Claimed</span>
                      <span>{roundToDecimalPlace(claimed.toString(), '8')}</span>
                    </div>
                  </div>
                </RewardItem>
              )
            })}
          </PoolContainer>

          <WarningContainer>
            Note: If you’ve already claimed your incentive rewards, the interface may take a few moments to update the
            remaining claimable amount.
          </WarningContainer>
        </>
      ) : (
        <p style={{ textAlign: 'center', marginTop: '0', marginBottom: '12px' }}>No pending rewards.</p>
      )}

      <RowBetween style={{ gap: '12px' }}>
        <ButtonPrimary disabled={loadingClaim || rewards?.length === 0} onClick={() => handleClaim()}>
          {loadingClaim ? <Dots>Claiming</Dots> : 'Claim all'}
        </ButtonPrimary>
        <ButtonLight onClick={onDismiss}>Cancel</ButtonLight>
      </RowBetween>
    </Container>
  )
}
